import { useEffect, useState } from "react";
import { Loader2, Plus, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

let TOPICS = null;
const useTopics = () => {
  const [t, setT] = useState(TOPICS || []);
  useEffect(() => { if (!TOPICS) api.get("/vesta/profilo/ai/topics").then((r) => { TOPICS = r.data.topics; setT(TOPICS); }).catch(() => {}); }, []);
  return t;
};
export const validSite = (w) => /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i.test((w || "").trim());

const TH = {
  dark: { inp: "w-full rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-base text-white outline-none focus:border-[#C17F59]",
    lbl: "mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-400", on: "border-[#C17F59] bg-[#C17F59] text-black", off: "border-neutral-700 text-neutral-300",
    btn: "border-[#C17F59] text-[#C17F59] hover:bg-[#C17F59] hover:text-black", val: "border-neutral-800 bg-neutral-900 text-white" },
  light: { inp: "w-full rounded-md border border-input bg-background px-3 py-2 text-base outline-none focus:border-primary sm:text-sm",
    lbl: "mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground", on: "border-primary bg-primary text-primary-foreground", off: "border-border text-foreground",
    btn: "border-primary text-primary hover:bg-primary hover:text-primary-foreground", val: "border-border bg-background" },
};

export function AiReadButton({ website, onResult, generate, theme = "dark" }) {
  const [busy, setBusy] = useState(false);
  if (!validSite(website)) return null;
  const go = async () => {
    setBusy(true);
    try { onResult(await generate(website)); toast.success("Profilo scritto con AI: controlla e salva"); }
    catch (e) { toast.error(e.response?.data?.detail || "Lettura del sito non riuscita"); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid="ai-profilo-read"
      className={`mt-2 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-60 ${TH[theme].btn}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
      {busy ? "Sto leggendo il sito…" : "Leggi con AI"}
    </button>
  );
}

export function AiProfiloFields({ f, setF, theme = "dark" }) {
  const t = TH[theme];
  const topics = useTopics();
  const [nv, setNv] = useState("");
  const toggle = (x) => setF({ ...f, topics: f.topics.includes(x) ? f.topics.filter((y) => y !== x) : [...f.topics, x].slice(0, 8) });
  const addV = () => { if (nv.trim()) { setF({ ...f, valori: [...f.valori, nv.trim()].slice(0, 6) }); setNv(""); } };
  return (
    <div className="space-y-4" data-testid="ai-profilo-fields">
      <div className="grid gap-3 sm:grid-cols-2">
        <div><div className={t.lbl}>Fondatore</div><input className={t.inp} value={f.fondatore} onChange={(e) => setF({ ...f, fondatore: e.target.value })} placeholder="Nome e cognome" data-testid="ai-profilo-fondatore" /></div>
        <div><div className={t.lbl}>Anno di fondazione</div><input className={t.inp} inputMode="numeric" maxLength={4} value={f.anno_fondazione} onChange={(e) => setF({ ...f, anno_fondazione: e.target.value.replace(/\D/g, "") })} placeholder="es. 1998" data-testid="ai-profilo-anno" /></div>
      </div>
      <div>
        <div className={t.lbl}>I nostri valori</div>
        <div className="space-y-1.5">
          {f.valori.map((v, i) => (
            <div key={i} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${t.val}`} data-testid={`ai-profilo-valore-${i}`}>
              <span className="flex-1">{v}</span>
              <button type="button" onClick={() => setF({ ...f, valori: f.valori.filter((_, j) => j !== i) })} className="opacity-60 hover:opacity-100" data-testid={`ai-profilo-valore-del-${i}`}><X className="h-4 w-4" /></button>
            </div>
          ))}
          {f.valori.length < 6 && (
            <div className="flex gap-2">
              <input className={t.inp} value={nv} onChange={(e) => setNv(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addV())} placeholder="Aggiungi un valore…" data-testid="ai-profilo-valore-input" />
              <button type="button" onClick={addV} className={`shrink-0 rounded-lg border px-3 ${t.btn}`} data-testid="ai-profilo-valore-add"><Plus className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className={t.lbl}>Topic (max 8)</div>
        <div className="flex flex-wrap gap-1.5" data-testid="ai-profilo-topics">
          {topics.map((x) => (
            <button key={x} type="button" onClick={() => toggle(x)} data-testid={`ai-profilo-topic-${x.replace(/\W+/g, "-").toLowerCase()}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${f.topics.includes(x) ? t.on : t.off}`}>{x}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
